import { ReactNode } from "react";
import { ExternalLink, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

interface EntityListRowProps {
  href?: string;
  // Leading visual: an AgentAvatar, a folder icon, etc.
  leading: ReactNode;
  title: string;
  description?: string | null;
  // Optional trailing badge next to the title (e.g. a disabled StatusBadge).
  badge?: ReactNode;
  // When provided, renders an owner-only remove button.
  onRemove?: () => void;
  removeLabel?: string;
  isRemoving?: boolean;
  className?: string;
}

/**
 * A single row in a project's attached datasets / agents list: a link (opening
 * in a new tab) with a leading visual, title + optional description/badge, and
 * an optional remove action revealed on hover.
 */
export const EntityListRow = ({
  href,
  leading,
  title,
  description,
  badge,
  onRemove,
  removeLabel,
  isRemoving,
  className,
}: EntityListRowProps) => {
  const innerContent = (
    <>
      {leading}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{title}</span>
          {badge}
        </div>
        {description && (
          <span className="truncate text-xs text-muted-foreground">{description}</span>
        )}
      </div>
      {href && (
        <ExternalLink className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      )}
    </>
  );

  return (
    <li className="group flex items-center gap-3 px-5 py-4 first:rounded-t-lg last:rounded-b-lg transition-colors hover:bg-accent/50">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cn("flex min-w-0 flex-1 items-center gap-3", className)}
        >
          {innerContent}
        </a>
      ) : (
        <div className={cn("flex min-w-0 flex-1 items-center gap-3", className)}>
          {innerContent}
        </div>
      )}
      {onRemove && (
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
          onClick={onRemove}
          disabled={isRemoving}
          aria-label={removeLabel}
        >
          <Trash2 className="size-4" />
        </Button>
      )}
    </li>
  );
};
